// The slides part's CRDT model on the client (plans/SLIDES_ARTEFACT.md "Model", AB#175, from
// spike B). The web cannot import backend source (CLAUDE.md rule A), so this is the web's copy of
// backend/domain/slides.ts (the plain-data types) and backend/adapters/room/slides-crdt.ts (the Yjs
// codec), pinned by shared/slides-model.fixture.json: both sides must build the same Y structure and
// read it the same way (src/slides/parity.test.ts here, adapters/room/slides-parity.test.ts there).
//
// One Y.Doc per part:
//   Y.Map('meta')          { kind: 'slides', v: 1 }
//   Y.Array('slideOrder')  slide ids
//   Y.Map('slides')        id → Y.Map { layout?, template?, notes: Y.Text, order: Y.Array<id>, elements: Y.Map<id, Y.Map> }
// Every element of a slide (grouped or not) is one Y.Map in the slide's flat `elements` map, with
// its own Y types, so two members editing two different elements never touch the same key. A
// group's `children` lists its members back to front; a member is absent from the slide's `order`.
// Member coordinates are slide coordinates. Rich text is a Y.XmlFragment in tiptap's schema.
//
// The client reads a slide FLAT (`SlideSnapshot`: `order` + an `elements` record with child ids),
// which is what the selection rules and the renderer walk; the backend reads the same slide as a
// tree. The tolerant read is the same on both sides, and the client never repairs the CRDT: only
// the server does (two clients repairing one merge would both re-insert the orphans).
import * as Y from 'yjs'
import { connectionSiteCount, isConnectorPreset, LIST_MAX_LEVEL, isPresetName, type PresetName } from '../lib/slide-presets'
import { ANCHOR_HREF_PREFIX, ANCHOR_ID_RE, anchorHref, readAnchor, sameAnchor, type Anchor } from '../lib/anchors'

export type { Anchor } from '../lib/anchors'

/** The `Y.Map('meta')` discriminator's schema version (backend SLIDES_SCHEMA_VERSION). */
export const SLIDES_SCHEMA_VERSION = 1

/** The logical slide canvas (16:9); the template's real size is applied at export. */
export const SLIDE_W = 1280
export const SLIDE_H = 720

export const ELEMENT_KINDS = ['text', 'shape', 'image', 'table', 'icon', 'group'] as const
export type ElementKind = (typeof ELEMENT_KINDS)[number]

// ── rich text, as plain data ────────────────────────────────────────────────────────────────────

export interface Run {
  text: string
  bold?: true
  italic?: true
  underline?: true
  /** A colour value: a theme token (`accent1`) or `#RRGGBB`. */
  color?: string
  /** The highlight behind the words, same value forms as `color`; independent of it and of the fill. */
  highlight?: string
  /** The size of these letters, in canvas px (AB#312); absent: the element's `style.fontSize`, the box default. */
  fontSize?: number
  /** A hyperlink on these letters (AB#327). */
  link?: Link
  /** A named anchor on these letters (AB#350): a link target, see lib/anchors.ts. */
  anchor?: Anchor
}
export type Inline = Run | { br: true }
export interface Paragraph {
  type: 'p'
  inlines: Inline[]
}
export interface List {
  type: 'ul' | 'ol'
  /** Each item is a list of blocks (paragraphs, nested lists). */
  items: Block[][]
  /**
   * Extra levels per item, on top of its nesting (AB#285): a level forced past what nesting can say
   * (the first item of a list, an item two levels below the one above). Absent or 0: none.
   */
  indents?: number[]
}
export type Block = Paragraph | List

const sameMarks = (a: Run, b: Run): boolean =>
  a.bold === b.bold && a.italic === b.italic && a.underline === b.underline && a.color === b.color && a.highlight === b.highlight && a.fontSize === b.fontSize &&
  sameLink(a.link, b.link) && sameAnchor(a.anchor, b.anchor)

/** Merge adjacent runs of the same formatting, drop empty ones: the canonical inline list. */
export function normalizeInlines(inlines: Inline[]): Inline[] {
  const out: Inline[] = []
  for (const inl of inlines) {
    if ('br' in inl) {
      out.push({ br: true })
      continue
    }
    if (inl.text === '') continue
    const prev = out[out.length - 1]
    if (prev && !('br' in prev) && sameMarks(prev, inl)) out[out.length - 1] = { ...prev, text: prev.text + inl.text }
    else out.push({ ...inl })
  }
  return out
}

// ── hyperlinks (AB#327) — backend domain/slides.ts, kept in parity ──────────────────────────────

/**
 * A hyperlink, on a text range (`Run.link`) or a whole element (`ElementSnapshot.link`): a web page,
 * an e-mail, or a slide of this deck — by its id, never its index, so reordering keeps it right —, or
 * an anchor of this artefact (AB#350), by its id too.
 */
export type Link =
  | { kind: 'url'; href: string }
  | { kind: 'mailto'; to: string; subject?: string }
  | { kind: 'slide'; slideId: string }
  | { kind: 'anchor'; anchorId: string }

/** The value forms, for error messages (backend LINK_FORMS). */
export const LINK_FORMS = 'https://…, http://…, mailto:<address>[?subject=…], #slide-<slideId>, #anchor-<anchorId>'

const EMAIL_RE = /^[A-Za-z0-9.!#$%&'*+/=^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/
const SLIDE_ID_RE = /^[A-Za-z0-9_-]{1,64}$/
const URL_MAX = 2048
const SUBJECT_MAX = 255

/** Why a link is refused, or null when it is valid: http(s) only, a well-formed address, a slide id. */
export function linkProblem(l: Link): string | null {
  switch (l.kind) {
    case 'url': {
      const href = l.href
      if (typeof href !== 'string' || href === '' || /\s/.test(href)) return 'a web link needs an address with no spaces'
      if (href.length > URL_MAX) return `a web link is at most ${URL_MAX} characters`
      let u: URL
      try {
        u = new URL(href)
      } catch {
        return `'${href}' is not a web address`
      }
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return `only http:// and https:// web links are allowed, not ${u.protocol}`
      return u.hostname ? null : `'${href}' has no host`
    }
    case 'mailto':
      if (typeof l.to !== 'string' || !EMAIL_RE.test(l.to)) return `'${String(l.to)}' is not an e-mail address`
      if (l.subject !== undefined && (typeof l.subject !== 'string' || l.subject.length > SUBJECT_MAX)) return `the subject is at most ${SUBJECT_MAX} characters`
      return null
    case 'slide':
      return typeof l.slideId === 'string' && SLIDE_ID_RE.test(l.slideId) ? null : `'${String(l.slideId)}' is not a slide id`
    case 'anchor':
      return typeof l.anchorId === 'string' && ANCHOR_ID_RE.test(l.anchorId) ? null : `'${String(l.anchorId)}' is not an anchor id`
    default:
      return 'unknown link kind'
  }
}

/** A stored link, read tolerantly (tiptap writes unset attributes as null): a clean valid link, else none. */
export function readLink(v: unknown): Link | undefined {
  if (typeof v !== 'object' || v === null) return undefined
  const o = v as Record<string, unknown>
  const str = (k: string): string | undefined => (typeof o[k] === 'string' ? (o[k] as string) : undefined)
  let l: Link
  if (o.kind === 'url') l = { kind: 'url', href: str('href') ?? '' }
  else if (o.kind === 'mailto') {
    const subject = str('subject')
    l = { kind: 'mailto', to: str('to') ?? '', ...(subject ? { subject } : {}) }
  } else if (o.kind === 'slide') l = { kind: 'slide', slideId: str('slideId') ?? '' }
  else if (o.kind === 'anchor') l = { kind: 'anchor', anchorId: str('anchorId') ?? '' }
  else return undefined
  return linkProblem(l) === null ? l : undefined
}

/** A link's canonical string form: the href, `mailto:to[?subject=…]`, `#slide-<id>`, `#anchor-<id>`. */
export function linkHref(l: Link): string {
  if (l.kind === 'url') return l.href
  if (l.kind === 'mailto') return `mailto:${l.to}${l.subject ? `?subject=${encodeURIComponent(l.subject)}` : ''}`
  if (l.kind === 'anchor') return anchorHref(l.anchorId)
  return `#slide-${l.slideId}`
}

/** A link's string form back into a link, validated (backend parseLinkHref). */
export function parseLinkHref(s: string): { ok: true; link: Link } | { ok: false; message: string } {
  const v = s.trim()
  const fail = (why: string): { ok: false; message: string } => ({ ok: false, message: `${why} (allowed: ${LINK_FORMS})` })
  let l: Link
  if (v.startsWith('#slide-')) l = { kind: 'slide', slideId: v.slice('#slide-'.length) }
  else if (v.startsWith(ANCHOR_HREF_PREFIX)) l = { kind: 'anchor', anchorId: v.slice(ANCHOR_HREF_PREFIX.length) }
  else if (/^mailto:/i.test(v)) {
    const rest = v.slice('mailto:'.length)
    const q = rest.indexOf('?')
    let to: string
    let subject: string | undefined
    try {
      to = decodeURIComponent(q < 0 ? rest : rest.slice(0, q))
      if (q >= 0) {
        for (const pair of rest.slice(q + 1).split('&')) {
          if (pair === '') continue
          const eq = pair.indexOf('=')
          const key = decodeURIComponent(eq < 0 ? pair : pair.slice(0, eq)).toLowerCase()
          if (key !== 'subject') return fail(`'${key}' is not allowed in a mailto link, only subject`)
          subject = decodeURIComponent(eq < 0 ? '' : pair.slice(eq + 1))
        }
      }
    } catch {
      return fail(`'${v}' is badly encoded`)
    }
    l = { kind: 'mailto', to, ...(subject ? { subject } : {}) }
  } else l = { kind: 'url', href: v }
  const problem = linkProblem(l)
  return problem ? fail(problem) : { ok: true, link: l }
}

/** Do two links lead to the same place? (Both absent: yes.) */
export function sameLink(a: Link | undefined, b: Link | undefined): boolean {
  if (!a || !b) return a === b
  return linkHref(a) === linkHref(b)
}

/** What a member typed in the Web field, as a link: `https://` is added when no scheme is given. */
export function webLinkFrom(input: string): Link {
  const v = input.trim()
  return { kind: 'url', href: /^[a-z][a-z0-9+.-]*:/i.test(v) && !/^[^:/]+:\d+(\/|$)/.test(v) ? v : `https://${v}` }
}

// ── elements ────────────────────────────────────────────────────────────────────────────────────

/** Element style, in canvas px and the restricted HTML's value forms. Every key is optional. */
export interface ElementStyle {
  fill?: string
  line?: string
  lineWidth?: number
  /** The outline's dash (backend LINE_DASHES); absent = solid. */
  lineDash?: LineDash
  /** A line's start / end termination (backend LINE_ENDS); absent = none. */
  lineHead?: LineEnd
  lineTail?: LineEnd
  radius?: number
  /** `Xpx Ypx Bpx <colour>`, canonical. */
  shadow?: string
  opacity?: number
  /**
   * The inner padding (AB#311): top right bottom left, in % of the box — top / bottom of its
   * height, left / right of its width —, so a resize scales it. Unset: DEFAULT_PADDING_PCT.
   * Read through paddingPx / storedPaddingPct.
   */
  paddingPct?: Padding
  /** Legacy inner padding in px (before AB#311): drawn as stored while `paddingPct` is unset. */
  padding?: Padding
  /** `major` | `minor` (theme fonts) or a family name */
  fontFamily?: string
  fontSize?: number
  fontWeight?: 400 | 700
  fontStyle?: 'italic'
  color?: string
  align?: 'left' | 'center' | 'right' | 'justify'
  valign?: 'top' | 'middle' | 'bottom'
  lineHeight?: number
  /** A connector's label (AB#324, its text): where along the route, in % from the start (0–100); absent = 50. */
  labelPos?: number
  /** A connector's label: above, on or below the line (LABEL_SIDES); absent = above. */
  labelSide?: LabelSide
}
export const STYLE_KEYS = [
  'fill', 'line', 'lineWidth', 'lineDash', 'lineHead', 'lineTail', 'radius', 'shadow', 'opacity', 'padding', 'paddingPct',
  'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'color', 'align', 'valign', 'lineHeight', 'labelPos', 'labelSide',
] as const satisfies ReadonlyArray<keyof ElementStyle>

// ── inner padding (AB#311) — backend domain/slides.ts, kept in parity ───────────────────────────

/** Four sides, top right bottom left (CSS order). */
export type Padding = [number, number, number, number]

/** The padding of a text box or a shape whose style sets none, in % (backend DEFAULT_PADDING_PCT). */
export const DEFAULT_PADDING_PCT: Padding = [4, 2, 4, 2]
/** The largest padding of one side, in %. */
export const PADDING_PCT_MAX = 50

/** Four finite non-negative numbers, else undefined (a malformed stored value reads as unset). */
export function readPadding(v: unknown): Padding | undefined {
  return Array.isArray(v) && v.length === 4 && v.every((n) => typeof n === 'number' && Number.isFinite(n) && n >= 0)
    ? ([...v] as Padding)
    : undefined
}

const pct2 = (n: number): number => Math.round(n * 100) / 100

/** A padding in px as % of a `w` × `h` box (2 decimals, capped at PADDING_PCT_MAX); 0 on an empty side. */
export function paddingToPct(px: Padding, w: number, h: number): Padding {
  const of = (v: number, size: number): number => (size > 0 ? Math.min(pct2((v / size) * 100), PADDING_PCT_MAX) : 0)
  return [of(px[0], h), of(px[1], w), of(px[2], h), of(px[3], w)]
}

/** A padding in % of a `w` × `h` box as px. */
export function paddingFromPct(pct: Padding, w: number, h: number): Padding {
  return [(pct[0] * h) / 100, (pct[1] * w) / 100, (pct[2] * h) / 100, (pct[3] * w) / 100]
}

/** The padding a style SETS, in % of its box: `paddingPct`, else the legacy px converted; undefined when none. */
export function storedPaddingPct(style: ElementStyle, w: number, h: number): Padding | undefined {
  const pct = readPadding(style.paddingPct)
  if (pct) return pct
  const px = readPadding(style.padding)
  return px ? paddingToPct(px, w, h) : undefined
}

/** The padding to draw, in px: `paddingPct` × the box, else the legacy px as stored, else the default. */
export function paddingPx(style: ElementStyle, w: number, h: number): Padding {
  const pct = readPadding(style.paddingPct)
  if (pct) return paddingFromPct(pct, w, h)
  return readPadding(style.padding) ?? paddingFromPct(DEFAULT_PADDING_PCT, w, h)
}

/** The padding the editor shows and edits, in %: what the style sets, else the default. */
export const paddingPctOf = (e: Pick<ElementSnapshot, 'style' | 'w' | 'h'>): Padding =>
  storedPaddingPct(e.style, e.w, e.h) ?? DEFAULT_PADDING_PCT

/** An outline's dash style (export: `a:prstDash` solid / dash / sysDot / dashDot / lgDash). */
export const LINE_DASHES = ['solid', 'dash', 'dot', 'dashDot', 'lgDash'] as const
export type LineDash = (typeof LINE_DASHES)[number]
/** A line's termination (export: `a:headEnd` / `a:tailEnd` type none / triangle / oval). */
export const LINE_ENDS = ['none', 'arrow', 'disk'] as const
export type LineEnd = (typeof LINE_ENDS)[number]

// ── connector labels (AB#324) — backend domain/slides.ts, kept in parity ────────────────────────

/** Where a connector's label sits across its line. */
export const LABEL_SIDES = ['above', 'on', 'below'] as const
export type LabelSide = (typeof LABEL_SIDES)[number]
/** The label's position along the route when unset, in %. */
export const DEFAULT_LABEL_POS = 50
/** The label's side when unset. */
export const DEFAULT_LABEL_SIDE: LabelSide = 'above'

/** A style's label position, 0–100 (clamped; the default when unset or malformed). */
export function labelPosOf(style: ElementStyle): number {
  const v = style.labelPos
  return typeof v === 'number' && Number.isFinite(v) ? Math.min(Math.max(v, 0), 100) : DEFAULT_LABEL_POS
}

/** A style's label side (the default when unset or malformed). */
export function labelSideOf(style: ElementStyle): LabelSide {
  return (LABEL_SIDES as readonly unknown[]).includes(style.labelSide) ? (style.labelSide as LabelSide) : DEFAULT_LABEL_SIDE
}

// ── one to three labels per connector (AB#332) — backend domain/slides.ts, kept in parity ──────
//
// Label 1 is the connector's own text, placed by `style.labelPos` / `style.labelSide` (AB#324), so
// every deck from before reads the same. Labels 2 and 3 are the element's `labels` Y.Array, one
// Y.Map each { text: Y.XmlFragment, pos?: number, side?: LabelSide } — a key older clients never
// read, so existing rooms load with no migration.

/** The most labels one connector carries. */
export const MAX_LABELS = 3
/** Where a new label goes along the route: the first of these no label of the connector is at. */
export const NEW_LABEL_POSITIONS = [50, 25, 75] as const

/** A connector's label 2 or 3, as plain data (`pos` / `side` absent: the defaults). */
export interface ConnectorLabel {
  text: Block[]
  pos?: number
  side?: LabelSide
}

/** The well-formed extra label maps of an element (a Y.Map with a text fragment), at most MAX_LABELS − 1, in order. */
function labelMaps(el: Y.Map<unknown>): Array<{ m: Y.Map<unknown>; at: number }> {
  const arr = el.get('labels')
  if (!(arr instanceof Y.Array)) return []
  const out: Array<{ m: Y.Map<unknown>; at: number }> = []
  arr.toArray().forEach((m: unknown, at: number) => {
    if (out.length < MAX_LABELS - 1 && m instanceof Y.Map && m.get('text') instanceof Y.XmlFragment) out.push({ m: m as Y.Map<unknown>, at })
  })
  return out
}

/** The extra labels (2 and 3) of an element map, read tolerantly (backend slides-crdt.ts#readLabels). */
export function readLabels(el: Y.Map<unknown>): ConnectorLabel[] {
  return labelMaps(el).map(({ m }) => {
    const l: ConnectorLabel = { text: readBlocks(m.get('text') as Y.XmlFragment) }
    const pos = m.get('pos')
    if (typeof pos === 'number' && Number.isFinite(pos)) l.pos = pos
    const side = m.get('side')
    if ((LABEL_SIDES as readonly unknown[]).includes(side)) l.side = side as LabelSide
    return l
  })
}

/** The Y.Map of a label 2 / 3, not yet attached (its text is filled once integrated). */
function labelMap(l: Omit<ConnectorLabel, 'text'>): Y.Map<unknown> {
  const m = new Y.Map<unknown>()
  m.set('text', new Y.XmlFragment())
  if (l.pos !== undefined) m.set('pos', l.pos)
  if (l.side !== undefined) m.set('side', l.side)
  return m
}

/** Write the extra labels of an element map already in the document (none: the key goes). */
export function writeLabels(el: Y.Map<unknown>, labels: readonly ConnectorLabel[]): void {
  const kept = labels.slice(0, MAX_LABELS - 1)
  if (kept.length === 0) {
    if (el.has('labels')) el.delete('labels')
    return
  }
  const arr = new Y.Array<Y.Map<unknown>>()
  el.set('labels', arr)
  arr.push(kept.map(labelMap))
  kept.forEach((l, i) => writeBlocks(arr.get(i).get('text') as Y.XmlFragment, l.text))
}

/** Does rich text hold a character (an empty paragraph does not)? */
export const hasText = (blocks: readonly Block[]): boolean =>
  blocks.some((b) => (b.type === 'p' ? b.inlines.some((i) => !('br' in i) && i.text !== '') : b.items.some(hasText)))

/**
 * Does a connector carry label 1? Its text holds a character, its position or side is set, or it
 * has other labels (label 1 is then the first of them).
 */
export const hasLabel = (e: Pick<ElementSnapshot, 'text' | 'style' | 'labels'>): boolean =>
  hasText(e.text ?? []) || e.style.labelPos !== undefined || e.style.labelSide !== undefined || (e.labels?.length ?? 0) > 0

/** How many labels a connector carries, 0 to MAX_LABELS. */
export const labelCount = (e: Pick<ElementSnapshot, 'text' | 'style' | 'labels'>): number =>
  hasLabel(e) ? 1 + Math.min(e.labels?.length ?? 0, MAX_LABELS - 1) : 0

/** One label of a connector: its number (1 to MAX_LABELS) and the connector as if it were its only label. */
export interface LabelView {
  n: number
  el: ElementSnapshot
}

/**
 * Every label of a connector, label 1 first — label 1 always, an empty one too (the editor's slot);
 * label n > 1 as the connector with that label's text and `labelPos` / `labelSide`, so the
 * one-label geometry (connectors.ts#labelAnchor, labelPlacement) places each.
 */
export function labelViews(e: ElementSnapshot): LabelView[] {
  return [
    { n: 1, el: e },
    ...(e.labels ?? []).slice(0, MAX_LABELS - 1).map((l, i) => {
      const style: ElementStyle = { ...e.style }
      delete style.labelPos
      delete style.labelSide
      if (l.pos !== undefined) style.labelPos = l.pos
      if (l.side !== undefined) style.labelSide = l.side
      return { n: i + 2, el: { ...e, text: l.text, style } }
    }),
  ]
}

/** Where a connector's next label goes: the first of NEW_LABEL_POSITIONS none of its labels is at. */
export function nextLabelPos(e: ElementSnapshot): number {
  const used = new Set(hasLabel(e) ? labelViews(e).map((v) => labelPosOf(v.el.style)) : [])
  return NEW_LABEL_POSITIONS.find((p) => !used.has(p)) ?? DEFAULT_LABEL_POS
}

/**
 * Set a connector's label `n` (1 by default) position (clamped to 0–100, whole %) and / or side —
 * one transaction, one undo step.
 */
export function setLabel(
  doc: Y.Doc,
  slideId: string,
  id: string,
  patch: { pos?: number; side?: LabelSide },
  origin: unknown = LOCAL_ORIGIN,
  n = 1,
): void {
  const pos = patch.pos !== undefined && Number.isFinite(patch.pos) ? Math.min(Math.max(Math.round(patch.pos), 0), 100) : undefined
  if (n <= 1) {
    const style: ElementStyle = {}
    if (pos !== undefined) style.labelPos = pos
    if (patch.side !== undefined) style.labelSide = patch.side
    if (Object.keys(style).length) setStyle(doc, slideId, [id], style, origin)
    return
  }
  if (pos === undefined && patch.side === undefined) return
  doc.transact(() => {
    const el = elementsOf(slideMap(doc, slideId)).get(id)
    const m = el ? labelMaps(el)[n - 2]?.m : undefined
    if (!m) return
    if (pos !== undefined) m.set('pos', pos)
    if (patch.side !== undefined) m.set('side', patch.side)
  }, origin)
}

/**
 * Add a label to a connector (AB#332) at the first free position of 50, 25, 75, above the line:
 * label 1 when it has none, else the next one. Its text is `text` as one paragraph (AB#335: the
 * editor passes its localised "Text", so the new label shows and can be double-clicked; none: empty).
 * One transaction. Returns the new label's number, 0 when the connector already has MAX_LABELS (or
 * is gone).
 */
export function addLabel(doc: Y.Doc, slideId: string, id: string, text = '', origin: unknown = LOCAL_ORIGIN): number {
  let added = 0
  doc.transact(() => {
    const el = elementsOf(slideMap(doc, slideId)).get(id)
    const style = el?.get('style')
    if (!el || !(style instanceof Y.Map)) return
    const snap = snapshotElement(id, el)
    const count = labelCount(snap)
    if (!isConnector(snap) || count >= MAX_LABELS) return
    const pos = nextLabelPos(snap)
    if (count === 0) {
      style.set('labelPos', pos)
      style.set('labelSide', DEFAULT_LABEL_SIDE)
      const frag = el.get('text')
      if (text && frag instanceof Y.XmlFragment) writeBlocks(frag, [{ type: 'p', inlines: [{ text }] }])
      added = 1
      return
    }
    let arr = el.get('labels')
    if (!(arr instanceof Y.Array)) {
      arr = new Y.Array<Y.Map<unknown>>()
      el.set('labels', arr)
    }
    const m = labelMap({ pos, side: DEFAULT_LABEL_SIDE })
    ;(arr as Y.Array<Y.Map<unknown>>).push([m])
    if (text) writeBlocks(m.get('text') as Y.XmlFragment, [{ type: 'p', inlines: [{ text }] }])
    added = labelMaps(el).length + 1
  }, origin)
  return added
}

/**
 * Remove a connector's label `n` — one transaction. Label 1 removed while others remain: label 2
 * takes its place (its text, position and side), so a connector with labels always has label 1;
 * the last label removed clears the text and the placement.
 */
export function removeLabel(doc: Y.Doc, slideId: string, id: string, n: number, origin: unknown = LOCAL_ORIGIN): void {
  doc.transact(() => {
    const el = elementsOf(slideMap(doc, slideId)).get(id)
    const style = el?.get('style')
    if (!el || !(style instanceof Y.Map)) return
    const arr = el.get('labels')
    const maps = labelMaps(el)
    const gone = n > 1 ? maps[n - 2] : maps[0]
    if (n <= 1) {
      const frag = el.get('text')
      const pos = gone?.m.get('pos')
      const side = gone?.m.get('side')
      if (frag instanceof Y.XmlFragment) {
        if (gone) writeBlocks(frag, readBlocks(gone.m.get('text') as Y.XmlFragment))
        else if (frag.length > 0) frag.delete(0, frag.length)
      }
      if (typeof pos === 'number' && Number.isFinite(pos)) style.set('labelPos', pos)
      else if (style.has('labelPos')) style.delete('labelPos')
      if ((LABEL_SIDES as readonly unknown[]).includes(side)) style.set('labelSide', side)
      else if (style.has('labelSide')) style.delete('labelSide')
    }
    if (gone && arr instanceof Y.Array) arr.delete(gone.at, 1)
    if (el.has('labels') && labelMaps(el).length === 0) el.delete('labels')
  }, origin)
}

/** The Y.XmlFragment of a connector's label `n` (1: its own text), if it exists. */
export function labelFragment(el: Y.Map<unknown> | undefined, n: number): Y.XmlFragment | null {
  const frag = !el ? undefined : n <= 1 ? el.get('text') : labelMaps(el)[n - 2]?.m.get('text')
  return frag instanceof Y.XmlFragment ? frag : null
}

/** The plain fields of an element map (everything but Y types). */
export const SCALAR_FIELDS = ['kind', 'x', 'y', 'w', 'h', 'rot', 'role', 'geom', 'src', 'alt', 'icon', 'header', 'from', 'to', 'adj', 'lockRatio', 'crop', 'origin', 'link', 'anchor'] as const

/**
 * A table's stored column weights, read tolerantly: an array of finite positive numbers, else none
 * (equal columns) — backend domain/slides.ts#readCols.
 */
export function readCols(v: unknown): number[] | undefined {
  return Array.isArray(v) && v.length > 0 && v.every((n) => typeof n === 'number' && Number.isFinite(n) && n > 0) ? [...(v as number[])] : undefined
}

/** A table's stored row weights, read exactly as `readCols` (else none: equal rows) — backend domain/slides.ts#readRows. */
export const readRows = readCols

/**
 * A table cell's own style (AB#333), overriding its table's: its background and where its text
 * sits — backend domain/slides.ts#CellStyle. Every key optional; `{}` = the cell follows the table.
 */
export interface CellStyle {
  fill?: string
  align?: 'left' | 'center' | 'right' | 'justify'
  valign?: 'top' | 'middle' | 'bottom'
}
export const CELL_STYLE_KEYS = ['fill', 'align', 'valign'] as const satisfies ReadonlyArray<keyof CellStyle>
const CELL_ALIGNS: readonly unknown[] = ['left', 'center', 'right', 'justify']
const CELL_VALIGNS: readonly unknown[] = ['top', 'middle', 'bottom']

/** One stored cell style, read tolerantly: only the well-formed keys — backend readCellStyle. */
export function readCellStyle(v: unknown): CellStyle {
  const out: CellStyle = {}
  if (!v || typeof v !== 'object' || Array.isArray(v)) return out
  const o = v as Record<string, unknown>
  if (typeof o.fill === 'string' && o.fill !== '') out.fill = o.fill
  if (CELL_ALIGNS.includes(o.align)) out.align = o.align as NonNullable<CellStyle['align']>
  if (CELL_VALIGNS.includes(o.valign)) out.valign = o.valign as NonNullable<CellStyle['valign']>
  return out
}

/**
 * A table's stored per-cell styles (AB#333): rows of cell styles laid out as its cells (a missing
 * one is `{}`), read tolerantly; none when no cell sets anything — backend readCellStyles.
 */
export function readCellStyles(v: unknown): CellStyle[][] | undefined {
  if (!Array.isArray(v)) return undefined
  const rows = v.map((row) => (Array.isArray(row) ? row.map(readCellStyle) : []))
  return rows.some((row) => row.some((c) => Object.keys(c).length > 0)) ? rows : undefined
}

// ── connectors ──────────────────────────────────────────────────────────────────────────────────

/**
 * Where one end of a connector (a `line` or `elbow` shape) is glued: an element of the same slide
 * and the index of one of its connection sites (lib/slide-presets.ts#connectionSites, PowerPoint's
 * `a:stCxn idx`) — backend domain/slides.ts#Binding. Ids are stable through group and ungroup.
 */
export interface Binding {
  el: string
  site: number
}

/** A stored binding, read tolerantly: `{ el: <id>, site: <integer ≥ 0> }`, else none. */
export function readBinding(v: unknown): Binding | undefined {
  if (typeof v !== 'object' || v === null) return undefined
  const { el, site } = v as { el?: unknown; site?: unknown }
  return typeof el === 'string' && el !== '' && typeof site === 'number' && Number.isInteger(site) && site >= 0 ? { el, site } : undefined
}

/** A stored elbow adjust value, read tolerantly: a finite number (rounded), else none (backend readAdj). */
export function readAdj(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : undefined
}

/**
 * An image's crop: the fractions 0–1 of the picture trimmed from its left, top, right and bottom
 * (PPTX `a:srcRect` semantics: l + r < 1, t + b < 1). Parity: backend domain/slides.ts#Crop.
 */
export interface Crop {
  l: number
  t: number
  r: number
  b: number
}

/** Is this a valid crop? Each side 0–1, and something left of the picture both ways (backend validCrop). */
export function validCrop(c: Crop): boolean {
  const ok = (n: number): boolean => Number.isFinite(n) && n >= 0 && n <= 1
  return ok(c.l) && ok(c.t) && ok(c.r) && ok(c.b) && c.l + c.r < 1 && c.t + c.b < 1
}

/** A stored crop, read tolerantly: four valid fractions with something trimmed, else none (backend readCrop). */
export function readCrop(v: unknown): Crop | undefined {
  if (typeof v !== 'object' || v === null) return undefined
  const { l, t, r, b } = v as Record<string, unknown>
  if (typeof l !== 'number' || typeof t !== 'number' || typeof r !== 'number' || typeof b !== 'number') return undefined
  const c = { l, t, r, b }
  return validCrop(c) && (l || t || r || b) ? c : undefined
}

/**
 * Where an imported cut-out picture comes from (plans/PPTX_IMPORT.md I5): the source element's bundle
 * in the blob store (its XML and package parts) and the box it was imported at (canvas px). Nothing
 * in the web reads it; it travels with the element, so deleting the picture drops it. Parity:
 * backend domain/slides.ts#ImageOrigin.
 */
export interface ImageOrigin {
  blobId: string
  box: Box
}

/** A stored origin, read tolerantly: a non-empty blob id and a box of finite numbers with a positive size, else none (backend readOrigin). */
export function readOrigin(v: unknown): ImageOrigin | undefined {
  if (typeof v !== 'object' || v === null) return undefined
  const { blobId, box } = v as Record<string, unknown>
  if (typeof blobId !== 'string' || blobId === '' || typeof box !== 'object' || box === null) return undefined
  const { x, y, w, h } = box as Record<string, unknown>
  const finite = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n)
  if (!finite(x) || !finite(y) || !finite(w) || !finite(h) || w <= 0 || h <= 0) return undefined
  return { blobId, box: { x, y, w, h } }
}

/** A connector: a `line` or `elbow` shape, bound or not. It has no connection site of its own. */
export const isConnector = (e: { kind: ElementKind; geom?: string }): boolean => e.kind === 'shape' && isConnectorPreset(e.geom)

/** The preset whose connection sites an element has: its shape's (rect when unknown), null (the rect's) for any other kind. */
export function sitePreset(e: { kind: ElementKind; geom?: string }): PresetName | null {
  if (e.kind !== 'shape') return null
  return isPresetName(e.geom) ? e.geom : 'rect'
}

/**
 * Can `b` glue an end of connector `id`? The element must be on the slide, not the connector itself
 * nor another connector, and have that site (backend domain/slides.ts#bindingProblem).
 */
export function bindingHolds(id: string, b: Binding, elements: Readonly<Record<string, ElementSnapshot>>): boolean {
  const t = elements[b.el]
  return t !== undefined && t.id !== id && !isConnector(t) && b.site < connectionSiteCount(sitePreset(t))
}

/** What "Regroup" restores, remembered on each member of an ungrouped group. */
export interface RegroupMemory {
  groupId: string
  members: string[]
}

export interface Box {
  x: number
  y: number
  w: number
  h: number
}

/** One element, read out of the CRDT. Coordinates are SLIDE coordinates, grouped or not. */
export interface ElementSnapshot extends Box {
  id: string
  kind: ElementKind
  rot: number
  style: ElementStyle
  /** text: the slide's title (backend `ELEMENT_ROLES`). A slide has one: a pasted copy does not take it. */
  role?: 'title'
  /** text, shape */
  text?: Block[]
  /** shape: rect, roundRect, ellipse, line, arrow, chevron, triangle, diamond, homePlate, elbow */
  geom?: string
  /**
   * A connector: the site its start / end is glued to. An unbound end is its stored point: the start
   * is the box's top-left corner, the end its bottom-right one, turned by `rot` (lineEnds).
   */
  from?: Binding
  to?: Binding
  /**
   * An elbow connector: where its middle segment sits, as bentConnector3's adj1 (1/100000 of the main
   * axis, from start to end) — set when a member drags it; absent = the default route.
   */
  adj?: number
  /** image: blob id */
  src?: string
  alt?: string
  /**
   * image: `false` = a resize may distort it. Absent = its aspect ratio is locked; only `false` is
   * ever stored (backend domain/slides.ts#ElementSnap.lockRatio). See `ratioLocked`.
   */
  lockRatio?: false
  /** image: what is trimmed from the picture (see `Crop`); absent = the whole picture. */
  crop?: Crop
  /** image: an imported cut-out's original (see `ImageOrigin`); absent on any other picture. */
  origin?: ImageOrigin
  /** table: rows × columns × blocks; `header` = the first row is a header row */
  cells?: Block[][][]
  header?: boolean
  /**
   * table: column widths as RELATIVE weights (canvas px when they were set); the renderer and the
   * export split the table's width in these proportions. Absent, or not one per column = equal.
   */
  cols?: number[]
  /**
   * table: row heights as RELATIVE weights (canvas px when they were set), read like `cols`: the
   * table's height is split in these proportions. Absent, or not one per row = equal rows.
   */
  rows?: number[]
  /** table: each cell's own fill and alignment (AB#333), rows × columns as `cells`; absent = all follow the table. */
  cellStyles?: CellStyle[][]
  /** icon: id in the Capgemini library */
  icon?: string
  /** group: member ids, back to front. */
  children?: string[]
  /** An ungrouped member: what "Regroup" restores. */
  regroup?: RegroupMemory
  /** A template draft's layout box whose size was set in the draft (AB#476): it stopped following its master's. */
  ownSize?: true
  /** A hyperlink on the whole element (AB#327). */
  link?: Link
  /** A named anchor on the whole element (AB#350): a link target. */
  anchor?: Anchor
  /** A connector's labels 2 and 3 (AB#332); label 1 is its `text`. */
  labels?: ConnectorLabel[]
}

export interface SlideSnapshot {
  id: string
  /**
   * The template layout the slide is drawn on: its id (`slideLayout7`); a slide created before
   * layout ids holds its type, resolved as a fallback (theme.ts `layoutFor`).
   */
  layout?: string
  /**
   * The bid Office template (PowerPoint) the slide is drawn on, by id (AB#445); absent = the deck
   * default. `layout` is a layout of THIS template: the pair names one layout, and so its master.
   */
  template?: string
  /** Top-level element ids, back to front. */
  order: string[]
  elements: Record<string, ElementSnapshot>
}

/** Union of boxes (rotation of members ignored: their unrotated boxes). */
export function unionBox(boxes: Box[]): Box {
  if (boxes.length === 0) return { x: 0, y: 0, w: 0, h: 0 }
  const x = Math.min(...boxes.map((b) => b.x))
  const y = Math.min(...boxes.map((b) => b.y))
  const r = Math.max(...boxes.map((b) => b.x + b.w))
  const btm = Math.max(...boxes.map((b) => b.y + b.h))
  return { x, y, w: r - x, h: btm - y }
}

/** Positions are stored to 1/100 px: enough for sub-pixel snapping, no float noise in the CRDT
 *  (`+ 0` turns a -0 into 0, so peers never diff on the sign of zero). */
export const round = (v: number): number => Math.round(v * 100) / 100 + 0

/** Text sizes (canvas px) are written and shown to 1/10 px (AB#417): a pt-converted size such as
 *  26.666666666666668 becomes 26.7. Existing decks are not migrated — the display rounds them. */
export const roundFontSize = (v: number): number => Math.round(v * 10) / 10 + 0

/** A text size as shown (label, tooltip, screen-reader text): at most one decimal, no trailing `.0`. */
export const formatFontSize = (v: number): string => String(roundFontSize(v))

/** The one size every value shares once rounded (26.666 and 26.7 are not mixed), else null (mixed or none). */
export function sharedFontSize(values: readonly number[]): number | null {
  if (!values.length) return null
  const first = roundFontSize(values[0]!)
  return values.every((v) => roundFontSize(v) === first) ? first : null
}

/** The transaction origin of this client's own edits — what its Y.UndoManager tracks. */
export const LOCAL_ORIGIN: object = { slides: 'local' }

// ── Y accessors ─────────────────────────────────────────────────────────────────────────────────

export const metaOf = (doc: Y.Doc): Y.Map<unknown> => doc.getMap('meta')
export const slideOrderOf = (doc: Y.Doc): Y.Array<string> => doc.getArray<string>('slideOrder')
export const slidesOf = (doc: Y.Doc): Y.Map<Y.Map<unknown>> => doc.getMap<Y.Map<unknown>>('slides')

/** Is this room a slides part? */
export const isSlidesDoc = (doc: Y.Doc): boolean => metaOf(doc).get('kind') === 'slides'

export function slideMap(doc: Y.Doc, slideId: string): Y.Map<unknown> {
  const s = slidesOf(doc).get(slideId)
  if (!s) throw new Error(`no slide ${slideId}`)
  return s
}
export const elementsOf = (slide: Y.Map<unknown>): Y.Map<Y.Map<unknown>> =>
  slide.get('elements') as Y.Map<Y.Map<unknown>>
export const orderOf = (slide: Y.Map<unknown>): Y.Array<string> => slide.get('order') as Y.Array<string>
export const childrenOf = (el: Y.Map<unknown>): Y.Array<string> | null =>
  (el.get('children') as Y.Array<string> | undefined) ?? null

/** Slide ids in deck order, read tolerantly: once each, existing only, orphan slides last (by id). */
export function slideIds(doc: Y.Doc): string[] {
  const slides = slidesOf(doc)
  const ids = [...new Set(slideOrderOf(doc).toArray())].filter((id) => slides.has(id))
  const listed = new Set(ids)
  return [...ids, ...[...slides.keys()].filter((id) => !listed.has(id)).sort()]
}

// ── rich text ↔ Y.XmlFragment (tiptap's schema) ──────────────────────────────────────────────────

function runMarks(r: Run): Record<string, unknown> {
  const m: Record<string, unknown> = {}
  if (r.bold) m.bold = {}
  if (r.italic) m.italic = {}
  if (r.underline) m.underline = {}
  // One textStyle mark carries both: the colour and the size of the letters (AB#312).
  if (r.color || r.fontSize) m.textStyle = { ...(r.color ? { color: r.color } : {}), ...(r.fontSize ? { fontSize: roundFontSize(r.fontSize) } : {}) }
  if (r.highlight) m.highlight = { color: r.highlight }
  if (r.link) m.link = { ...r.link }
  if (r.anchor) m.anchor = { ...r.anchor }
  return m
}

function paragraphToY(p: Paragraph): Y.XmlElement {
  const para = new Y.XmlElement('paragraph')
  const kids: Array<Y.XmlText | Y.XmlElement> = []
  let text: Y.XmlText | null = null
  let at = 0
  for (const inl of p.inlines) {
    if ('br' in inl) {
      kids.push(new Y.XmlElement('hardBreak'))
      text = null
      continue
    }
    if (!text) {
      text = new Y.XmlText()
      kids.push(text)
      at = 0
    }
    // A prelim Y.XmlText queues its inserts until it is integrated, so the formatting survives.
    text.insert(at, inl.text, runMarks(inl))
    at += inl.text.length
  }
  para.insert(0, kids)
  return para
}

function blockToY(b: Block): Y.XmlElement {
  if (b.type === 'p') return paragraphToY(b)
  const list = new Y.XmlElement(b.type === 'ul' ? 'bulletList' : 'orderedList')
  list.insert(
    0,
    b.items.map((item, i) => {
      const li = new Y.XmlElement('listItem')
      const indent = b.indents?.[i] ?? 0
      if (indent > 0) li.setAttribute('indent', indent as never)
      li.insert(0, item.map(blockToY))
      return li
    }),
  )
  return list
}

/** Replace a fragment's content with `blocks`, the way tiptap would have written them. */
export function writeBlocks(frag: Y.XmlFragment, blocks: Block[]): void {
  if (frag.length > 0) frag.delete(0, frag.length)
  if (blocks.length > 0) frag.insert(0, blocks.map(blockToY))
}

type DeltaOp = { insert?: unknown; attributes?: Record<string, unknown> }

function deltaRun(op: DeltaOp): Run | null {
  if (typeof op.insert !== 'string') return null
  const a = op.attributes ?? {}
  const r: Run = { text: op.insert }
  if (a.bold) r.bold = true
  if (a.italic) r.italic = true
  if (a.underline) r.underline = true
  const style = a.textStyle as { color?: unknown; fontSize?: unknown } | undefined
  if (typeof style?.color === 'string' && style.color) r.color = style.color
  if (typeof style?.fontSize === 'number' && style.fontSize > 0) r.fontSize = style.fontSize
  const highlight = (a.highlight as { color?: unknown } | undefined)?.color
  if (typeof highlight === 'string' && highlight) r.highlight = highlight
  const link = readLink(a.link)
  if (link) r.link = link
  const anchor = readAnchor(a.anchor)
  if (anchor) r.anchor = anchor
  return r
}

function readParagraph(el: Y.XmlElement): Paragraph {
  const inlines: Inline[] = []
  for (const k of el.toArray()) {
    if (k instanceof Y.XmlText) {
      for (const op of k.toDelta() as DeltaOp[]) {
        const r = deltaRun(op)
        if (r) inlines.push(r)
      }
    } else if (k instanceof Y.XmlElement && k.nodeName === 'hardBreak') inlines.push({ br: true })
  }
  return { type: 'p', inlines: normalizeInlines(inlines) }
}

/** A list item's `indent` attribute (AB#285) as a level count: 0 when absent or malformed. */
export const itemIndent = (v: unknown): number => {
  const n = Math.floor(Number(v))
  return Number.isFinite(n) ? Math.min(Math.max(n, 0), LIST_MAX_LEVEL) : 0
}

/**
 * A Y.XmlFragment written by tiptap (y-prosemirror: element names are ProseMirror node names, marks
 * are Y.XmlText format attributes) → plain blocks. Unknown nodes are skipped, as on the backend.
 */
export function readBlocks(frag: Y.XmlFragment | Y.XmlElement): Block[] {
  const out: Block[] = []
  for (const k of frag.toArray()) {
    if (!(k instanceof Y.XmlElement)) continue
    if (k.nodeName === 'paragraph') out.push(readParagraph(k))
    else if (k.nodeName === 'bulletList' || k.nodeName === 'orderedList') {
      const lis = k.toArray().filter((li): li is Y.XmlElement => li instanceof Y.XmlElement && li.nodeName === 'listItem')
      const list: List = { type: k.nodeName === 'bulletList' ? 'ul' : 'ol', items: lis.map((li) => readBlocks(li)) }
      const indents = lis.map((li) => itemIndent(li.getAttribute('indent')))
      if (indents.some((n) => n > 0)) list.indents = indents
      out.push(list)
    }
  }
  return out
}

/** Replace a table element's cells (the element must already be part of the document). */
export function writeCells(el: Y.Map<unknown>, cells: Block[][][]): void {
  const rows = el.get('cells') as Y.Array<Y.Array<Y.XmlFragment>>
  if (rows.length > 0) rows.delete(0, rows.length)
  rows.insert(0, cells.map((row) => Y.Array.from(row.map(() => new Y.XmlFragment()))))
  cells.forEach((row, r) => row.forEach((blocks, c) => writeBlocks(rows.get(r).get(c), blocks)))
}

export function readCells(el: Y.Map<unknown>): Block[][][] {
  const rows = el.get('cells') as Y.Array<Y.Array<Y.XmlFragment>> | undefined
  return rows ? rows.toArray().map((row) => row.toArray().map((f) => readBlocks(f))) : []
}

// ── reading ─────────────────────────────────────────────────────────────────────────────────────

export function readStyle(el: Y.Map<unknown>): ElementStyle {
  const style = el.get('style') as Y.Map<unknown> | undefined
  const out: Record<string, unknown> = {}
  if (style instanceof Y.Map) for (const k of STYLE_KEYS) if (style.has(k)) out[k] = style.get(k)
  return out as ElementStyle
}

/** One element map as plain data, as stored (a group's `children` and box are set by the read). */
export function snapshotElement(id: string, el: Y.Map<unknown>): ElementSnapshot {
  const kind = el.get('kind') as ElementKind
  const snap: ElementSnapshot = {
    id,
    kind,
    x: Number(el.get('x') ?? 0),
    y: Number(el.get('y') ?? 0),
    w: Number(el.get('w') ?? 0),
    h: Number(el.get('h') ?? 0),
    rot: Number(el.get('rot') ?? 0),
    style: readStyle(el),
  }
  if (kind === 'text' && el.get('role') === 'title') snap.role = 'title'
  for (const f of ['geom', 'src', 'alt', 'icon'] as const) {
    const v = el.get(f)
    if (typeof v === 'string') snap[f] = v
  }
  if (isConnector(snap)) {
    for (const end of ['from', 'to'] as const) {
      const b = readBinding(el.get(end))
      if (b) snap[end] = b
    }
    const adj = snap.geom === 'elbow' ? readAdj(el.get('adj')) : undefined
    if (adj !== undefined) snap.adj = adj
    const labels = readLabels(el)
    if (labels.length) snap.labels = labels
  }
  if (kind === 'image' && el.get('lockRatio') === false) snap.lockRatio = false
  const crop = kind === 'image' ? readCrop(el.get('crop')) : undefined
  if (crop) snap.crop = crop
  const origin = kind === 'image' ? readOrigin(el.get('origin')) : undefined
  if (origin) snap.origin = origin
  const text = el.get('text')
  if (text instanceof Y.XmlFragment) snap.text = readBlocks(text)
  if (kind === 'table') {
    snap.cells = readCells(el)
    snap.header = el.get('header') === true
    const cols = readCols(el.get('cols'))
    if (cols) snap.cols = cols
    const rows = readRows(el.get('rows'))
    if (rows) snap.rows = rows
    const cellStyles = readCellStyles(el.get('cellStyles'))
    if (cellStyles) snap.cellStyles = cellStyles
  }
  const regroup = el.get('regroup') as RegroupMemory | undefined
  if (regroup) snap.regroup = regroup
  if (el.get('ownSize') === true) snap.ownSize = true
  const link = readLink(el.get('link'))
  if (link) snap.link = link
  const anchor = readAnchor(el.get('anchor'))
  if (anchor) snap.anchor = anchor
  return snap
}

/**
 * The slide as plain data, repaired ON READ so every peer renders the same slide whatever
 * concurrent structure edits left in the CRDT (the same rules as the backend's `readSlide`):
 * - an id listed twice (two members grouping overlapping selections, the same id in `order` twice)
 *   is shown once, where depth-first z-order first meets it;
 * - a listed id with no element (a concurrent delete) is skipped;
 * - an element listed nowhere (an orphan) is shown on top, by id, so nothing is lost;
 * - a group's box is DERIVED from its members (the stored one is a cache that two concurrent member
 *   moves can leave stale).
 * - a connector's binding that cannot hold (a deleted element, a site out of range, itself, another
 *   connector) is shown unbound, its end at its stored point (the server's repair removes it).
 * A group left with one member still reads as a group: the server's repair dissolves it.
 */
export function snapshotSlide(doc: Y.Doc, slideId: string): SlideSnapshot {
  const slide = slideMap(doc, slideId)
  const els = elementsOf(slide)
  const elements: Record<string, ElementSnapshot> = {}
  const visit = (id: string): boolean => {
    const m = els.get(id)
    if (!m || id in elements) return false
    const snap = snapshotElement(id, m)
    elements[id] = snap
    if (snap.kind === 'group') {
      snap.children = (childrenOf(m)?.toArray() ?? []).filter(visit)
      if (snap.children.length > 0) Object.assign(snap, unionBox(snap.children.map((c) => elements[c] as Box)))
    }
    return true
  }
  const order = orderOf(slide).toArray().filter(visit)
  for (const id of [...els.keys()].sort()) if (visit(id)) order.push(id)
  for (const e of Object.values(elements)) {
    for (const end of ['from', 'to'] as const) {
      const b = e[end]
      if (b && !bindingHolds(e.id, b, elements)) delete e[end]
    }
  }
  const layout = slide.get('layout')
  const template = slide.get('template')
  return {
    id: slideId,
    ...(typeof layout === 'string' ? { layout } : {}),
    ...(typeof template === 'string' && template ? { template } : {}),
    order,
    elements,
  }
}

// ── writing ─────────────────────────────────────────────────────────────────────────────────────

let counter = 0
/** A short id that cannot collide across peers (client id + a local counter). */
export function newId(doc: Y.Doc, prefix: string): string {
  counter += 1
  return `${prefix}${doc.clientID.toString(36)}${counter.toString(36)}`
}

/** A new leaf element (groups are made by `groupElements`). */
export interface NewElement extends Box {
  kind: Exclude<ElementKind, 'group'>
  rot?: number
  style?: ElementStyle
  /** text: the slide's title; one per slide (the slide codec refuses two) */
  role?: 'title'
  geom?: string
  /** a connector's glued ends */
  from?: Binding
  to?: Binding
  adj?: number
  src?: string
  alt?: string
  /** image: only `false` is stored (absent = locked) */
  lockRatio?: false
  crop?: Crop
  /** image: an imported cut-out's original (the backend's import writes it; see `ImageOrigin`) */
  origin?: ImageOrigin
  icon?: string
  header?: boolean
  /** text, shape */
  text?: Block[]
  /** table */
  cells?: Block[][][]
  cols?: number[]
  rows?: number[]
  cellStyles?: CellStyle[][]
  /** a hyperlink on the whole element (AB#327) */
  link?: Link
  /** a named anchor on the whole element (AB#350) — never on a duplicate or a paste: ids stay unique */
  anchor?: Anchor
  /** a connector's labels 2 and 3 (AB#332) */
  labels?: ConnectorLabel[]
}

/** The Y.Map of a new element (not yet attached): the same keys the backend's codec writes. */
export function buildElement(spec: NewElement): Y.Map<unknown> {
  const el = new Y.Map<unknown>()
  const scalars: Record<string, unknown> = { ...spec, rot: spec.rot ?? 0 }
  for (const f of SCALAR_FIELDS) if (scalars[f] !== undefined) el.set(f, scalars[f])
  const style = new Y.Map<unknown>()
  for (const k of STYLE_KEYS) if (spec.style?.[k] !== undefined) style.set(k, spec.style[k])
  el.set('style', style)
  // Content is written once the element is integrated into the doc (see addElement).
  if (spec.kind === 'text' || spec.kind === 'shape') el.set('text', new Y.XmlFragment())
  if (spec.kind === 'table') el.set('cells', new Y.Array<Y.Array<Y.XmlFragment>>())
  if (spec.kind === 'table' && spec.cols) el.set('cols', spec.cols.map(round))
  if (spec.kind === 'table' && spec.rows) el.set('rows', spec.rows.map(round))
  if (spec.kind === 'table' && spec.cellStyles) el.set('cellStyles', spec.cellStyles.map((row) => row.map((c) => ({ ...c }))))
  return el
}

/** Add an element on top of the slide (or at `index` in its order); returns its id. */
export function addElement(
  doc: Y.Doc,
  slideId: string,
  spec: NewElement,
  id = newId(doc, 'e'),
  index?: number,
  origin: unknown = LOCAL_ORIGIN,
): string {
  doc.transact(() => {
    const slide = slideMap(doc, slideId)
    const el = buildElement(spec)
    elementsOf(slide).set(id, el)
    const frag = el.get('text')
    if (frag instanceof Y.XmlFragment && spec.text?.length) writeBlocks(frag, spec.text)
    if (spec.kind === 'table') writeCells(el, spec.cells ?? [])
    if (spec.labels?.length && isConnector(spec)) writeLabels(el, spec.labels)
    const order = orderOf(slide)
    order.insert(Math.min(index ?? order.length, order.length), [id])
  }, origin)
  return id
}

/** Add an empty slide (at the end, or at `index`); returns its id. Stamps the discriminator. */
export function addSlide(
  doc: Y.Doc,
  id = newId(doc, 's'),
  opts: { layout?: string; template?: string; index?: number } = {},
  origin: unknown = LOCAL_ORIGIN,
): string {
  doc.transact(() => {
    const meta = metaOf(doc)
    if (meta.get('kind') !== 'slides') meta.set('kind', 'slides')
    if (meta.get('v') !== SLIDES_SCHEMA_VERSION) meta.set('v', SLIDES_SCHEMA_VERSION)
    const slide = new Y.Map<unknown>()
    slidesOf(doc).set(id, slide)
    if (opts.layout) slide.set('layout', opts.layout)
    if (opts.template) slide.set('template', opts.template)
    slide.set('notes', new Y.Text())
    slide.set('order', new Y.Array<string>())
    slide.set('elements', new Y.Map<Y.Map<unknown>>())
    const order = slideOrderOf(doc)
    order.insert(Math.min(opts.index ?? order.length, order.length), [id])
  }, origin)
  return id
}

/** Patch an element's box / rotation. */
export function setBox(
  doc: Y.Doc,
  slideId: string,
  id: string,
  patch: Partial<Box & { rot: number }>,
  origin: unknown = LOCAL_ORIGIN,
): void {
  doc.transact(() => {
    const el = elementsOf(slideMap(doc, slideId)).get(id)
    if (!el) return
    for (const [k, v] of Object.entries(patch)) if (typeof v === 'number') el.set(k, round(v))
  }, origin)
}

/** Does resizing this element keep its aspect ratio? An image's, unless unlocked; nothing else's. */
export const ratioLocked = (e: Pick<ElementSnapshot, 'kind' | 'lockRatio'>): boolean => e.kind === 'image' && e.lockRatio !== false

/** Lock or unlock an image's aspect ratio. Only `false` is stored: a locked image has no key. */
export function setLockRatio(doc: Y.Doc, slideId: string, id: string, lock: boolean, origin: unknown = LOCAL_ORIGIN): void {
  doc.transact(() => {
    const el = elementsOf(slideMap(doc, slideId)).get(id)
    if (el?.get('kind') !== 'image') return
    if (!lock) el.set('lockRatio', false)
    else if (el.has('lockRatio')) el.delete('lockRatio')
  }, origin)
}

/**
 * Crop an image (null or nothing trimmed: the whole picture), its fractions rounded to what the
 * export and the restricted HTML write (1/100000). The box is the caller's (crop mode moves it).
 */
export function setCrop(doc: Y.Doc, slideId: string, id: string, crop: Crop | null, origin: unknown = LOCAL_ORIGIN): void {
  doc.transact(() => {
    const el = elementsOf(slideMap(doc, slideId)).get(id)
    if (el?.get('kind') !== 'image') return
    const r = (v: number): number => Math.round(v * 100000) / 100000 + 0
    const c = crop ? { l: r(crop.l), t: r(crop.t), r: r(crop.r), b: r(crop.b) } : null
    if (c && validCrop(c) && (c.l || c.t || c.r || c.b)) el.set('crop', c)
    else if (el.has('crop')) el.delete('crop')
  }, origin)
}

/** Patch the style of several elements (one transaction). */
export function setStyle(
  doc: Y.Doc,
  slideId: string,
  ids: string[],
  patch: ElementStyle,
  origin: unknown = LOCAL_ORIGIN,
): void {
  doc.transact(() => {
    const els = elementsOf(slideMap(doc, slideId))
    for (const id of ids) {
      const style = els.get(id)?.get('style')
      if (style instanceof Y.Map) for (const [k, v] of Object.entries(patch)) style.set(k, v)
    }
  }, origin)
}

/** A template draft's layout box takes a size of its own: it no longer follows its master's (AB#476). */
export function setOwnSize(doc: Y.Doc, slideId: string, id: string, origin: unknown = LOCAL_ORIGIN): void {
  doc.transact(() => elementsOf(slideMap(doc, slideId)).get(id)?.set('ownSize', true), origin)
}

/**
 * Put a hyperlink on several whole elements, or remove it (null) — one transaction (AB#327). An
 * invalid link is refused: nothing is written.
 */
export function setElementLink(doc: Y.Doc, slideId: string, ids: string[], link: Link | null, origin: unknown = LOCAL_ORIGIN): void {
  if (link && linkProblem(link) !== null) return
  doc.transact(() => {
    const els = elementsOf(slideMap(doc, slideId))
    for (const id of ids) {
      const el = els.get(id)
      if (!el) continue
      if (link) el.set('link', { ...link })
      else if (el.has('link')) el.delete('link')
    }
  }, origin)
}

/**
 * Put a named anchor on one whole element (create, or rename: same id, new name), or remove it
 * (null) — one transaction (AB#350). An invalid anchor is refused: nothing is written. The name's
 * uniqueness in the artefact is the caller's (the Anchor dialog checks it).
 */
export function setElementAnchor(doc: Y.Doc, slideId: string, id: string, anchor: Anchor | null, origin: unknown = LOCAL_ORIGIN): void {
  if (anchor && readAnchor(anchor)?.name !== anchor.name) return
  doc.transact(() => {
    const el = elementsOf(slideMap(doc, slideId)).get(id)
    if (!el) return
    if (anchor) el.set('anchor', { id: anchor.id, name: anchor.name })
    else if (el.has('anchor')) el.delete('anchor')
  }, origin)
}

/** Some blocks with the anchors of their letters dropped where `drop(id)` says so (AB#350). */
export function dropRunAnchors(blocks: readonly Block[], drop: (id: string) => boolean): Block[] {
  return blocks.map((b): Block => {
    if (b.type === 'p') {
      const inlines = b.inlines.map((inl): Inline => {
        if ('br' in inl || !inl.anchor || !drop(inl.anchor.id)) return inl
        const { anchor: _a, ...rest } = inl
        return rest
      })
      return { ...b, inlines: normalizeInlines(inlines) }
    }
    return { ...b, items: b.items.map((item) => dropRunAnchors(item, drop)) }
  })
}

/**
 * Set sides of the inner padding of several text boxes / shapes, in % (AB#311): `sides` maps a side
 * index (0 top, 1 right, 2 bottom, 3 left) to its value; each element keeps its own other sides.
 * Stored as `paddingPct` (clamped to 0 … PADDING_PCT_MAX), the legacy px one dropped — one transaction.
 */
export function setPadding(
  doc: Y.Doc,
  slideId: string,
  ids: string[],
  sides: Partial<Record<0 | 1 | 2 | 3, number>>,
  origin: unknown = LOCAL_ORIGIN,
): void {
  doc.transact(() => {
    const els = elementsOf(slideMap(doc, slideId))
    for (const id of ids) {
      const m = els.get(id)
      const style = m?.get('style')
      if (!m || !(style instanceof Y.Map)) continue
      const pct = paddingPctOf(snapshotElement(id, m))
      for (const [i, v] of Object.entries(sides)) pct[Number(i)] = Math.min(Math.max(pct2(v), 0), PADDING_PCT_MAX)
      style.set('paddingPct', pct)
      if (style.has('padding')) style.delete('padding')
    }
  }, origin)
}

/**
 * Before a resize: a legacy px padding (before AB#311) becomes % of the element's CURRENT box, so the
 * resize scales it from what the member sees. Call inside the resize's transaction.
 */
export function migratePadding(m: Y.Map<unknown> | undefined): void {
  const style = m?.get('style')
  if (!m || !(style instanceof Y.Map) || style.has('paddingPct')) return
  const px = readPadding(style.get('padding'))
  if (!px) return
  const w = Number(m.get('w')) || 0
  const h = Number(m.get('h')) || 0
  style.set('paddingPct', paddingToPct(px, w, h))
  style.delete('padding')
}
